import { useEffect, useRef, useState } from 'react'

import { Button } from '../../ui/Control'
import type { Draft } from './Grid'
import { type Repetition, RepeatFields, defaultUntil, weekdayOfDay } from './RepeatFields'
import { hereZone, minutesOf, momentAt, readableDay } from './week'

/**
 * What opens where a stretch was drawn.
 *
 * One day, two times, a title — that is the ordinary appointment, and it is all that is on the
 * screen until something else is asked for. Everything rarer sits behind a chip: whole days, more
 * than one day, a repetition. A form that showed all of them at once would ask six questions to
 * write down a lecture.
 *
 * **Nothing is written until it is saved.** A stretch drawn and then thought better of leaves
 * nothing behind. Escape and a click outside both mean the same thing.
 */
export interface Wanted {
  readonly title: string
  readonly startsAt: string
  readonly endsAt: string
  readonly allDay: boolean
  /** How it repeats, or `null` for the ordinary case of once. */
  readonly repetition: Repetition | null
}

export interface QuickAppointmentProps {
  readonly draft: Draft
  /** Writes it down and stays in the week. */
  readonly onCreate: (wanted: Wanted) => void
  /** Writes it down and opens it, for the room, the subject and the rest. */
  readonly onMore: (wanted: Wanted) => void
  readonly onClose: () => void
}

export function QuickAppointment({ draft, onCreate, onMore, onClose }: QuickAppointmentProps) {
  const zone = hereZone()

  const [title, setTitle] = useState('')
  const [day, setDay] = useState(draft.day)
  const [lastDay, setLastDay] = useState(draft.untilDay)
  const [from, setFrom] = useState(() => clock(draft.allDay ? 8 * 60 : draft.from))
  const [to, setTo] = useState(() => clock(draft.allDay ? 9 * 60 + 30 : draft.to))

  const [allDay, setAllDay] = useState(draft.allDay)
  const [severalDays, setSeveralDays] = useState(draft.day !== draft.untilDay)
  const [repeats, setRepeats] = useState(false)
  const [repetition, setRepetition] = useState<Repetition>(() => ({
    weekdays: [weekdayOfDay(draft.day)],
    everyWeeks: 1,
    until: defaultUntil(draft.day),
  }))

  const field = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    field.current?.focus()
  }, [])

  const until = severalDays ? lastDay : day
  const ready = title.trim() !== '' && day !== '' && until >= day

  const wanted = (): Wanted => ({
    title,
    startsAt: momentAt(day, allDay ? 0 : minutesOf(from), zone).toISOString(),
    /*
      A whole-day stretch ends at midnight *after* its last day. The end is exclusive, which is what
      makes "Monday to Wednesday" three days rather than two and a bit.
    */
    endsAt: allDay
      ? momentAt(dayAfter(until), 0, zone).toISOString()
      : momentAt(until, minutesOf(to), zone).toISOString(),
    allDay,
    repetition: repeats ? repetition : null,
  })

  return (
    <>
      {/*
        A surface that catches the click that means "not this after all". A control rather than a
        bare div, so that it is reachable and says what it does instead of being an invisible trap.
      */}
      <button type="button" className="quick__away" aria-label="Discard" onClick={onClose} />
      <div
        className="quick"
        role="dialog"
        aria-label="New appointment"
        style={placed(draft)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            onClose()
          }
        }}
      >
        <p className="quick__when">{readableDay(draft.day)}</p>

        <form
          className="quick__write"
          onSubmit={(event) => {
            event.preventDefault()
            if (ready) {
              onCreate(wanted())
            }
          }}
        >
          <label className="visually-hidden" htmlFor="quick-title">
            What is this
          </label>
          <input
            id="quick-title"
            ref={field}
            className="quick__field"
            value={title}
            placeholder="Lecture, study group, cinema"
            onChange={(event) => {
              setTitle(event.target.value)
            }}
          />

          <div className="quick__times">
            <div className="quick__time">
              <label className="quick__label" htmlFor="quick-day">
                {severalDays ? 'First day' : 'Day'}
              </label>
              <input
                id="quick-day"
                type="date"
                className="quick__field"
                value={day}
                onChange={(event) => {
                  setDay(event.target.value)
                }}
              />
            </div>
            {severalDays && (
              <div className="quick__time">
                <label className="quick__label" htmlFor="quick-last-day">
                  Last day
                </label>
                <input
                  id="quick-last-day"
                  type="date"
                  className="quick__field"
                  value={lastDay}
                  onChange={(event) => {
                    setLastDay(event.target.value)
                  }}
                />
              </div>
            )}
            {!allDay && (
              <>
                <div className="quick__time">
                  <label className="quick__label" htmlFor="quick-from">
                    From
                  </label>
                  <input
                    id="quick-from"
                    type="time"
                    className="quick__field"
                    value={from}
                    onChange={(event) => {
                      setFrom(event.target.value)
                    }}
                  />
                </div>
                <div className="quick__time">
                  <label className="quick__label" htmlFor="quick-to">
                    To
                  </label>
                  <input
                    id="quick-to"
                    type="time"
                    className="quick__field"
                    value={to}
                    onChange={(event) => {
                      setTo(event.target.value)
                    }}
                  />
                </div>
              </>
            )}
          </div>

          {/* The rarer shapes, one chip each. The ordinary appointment needs none of them. */}
          <div className="quick__more" role="group" aria-label="How long it lasts">
            <button
              type="button"
              className="chip"
              aria-pressed={allDay}
              onClick={() => {
                setAllDay((before) => !before)
              }}
            >
              All day
            </button>
            <button
              type="button"
              className="chip"
              aria-pressed={severalDays}
              onClick={() => {
                setSeveralDays((before) => {
                  if (!before) {
                    setLastDay(day)
                  }
                  return !before
                })
              }}
            >
              Several days
            </button>
            <button
              type="button"
              className="chip"
              aria-pressed={repeats}
              onClick={() => {
                setRepeats((before) => !before)
              }}
            >
              Repeats
            </button>
          </div>

          {repeats && <RepeatFields value={repetition} onChange={setRepetition} />}

          <div className="controls controls--tight">
            <Button tone="primary" type="submit" disabled={!ready}>
              Write it down
            </Button>
            <Button
              tone="bare"
              disabled={!ready}
              onClick={() => {
                onMore(wanted())
              }}
            >
              All details
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}

/** Minutes past midnight as `HH:MM`, which is what a time field speaks. */
function clock(minute: number): string {
  return `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`
}

/** The day after this one, as `YYYY-MM-DD`. */
function dayAfter(day: string): string {
  const at = new Date(`${day}T12:00:00Z`)
  at.setUTCDate(at.getUTCDate() + 1)
  const month = String(at.getUTCMonth() + 1).padStart(2, '0')
  const date = String(at.getUTCDate()).padStart(2, '0')
  return `${String(at.getUTCFullYear())}-${month}-${date}`
}

/**
 * Beside the stretch, and never off the screen.
 *
 * Fixed rather than absolute, because the grid it was drawn on scrolls: a sheet that scrolled away
 * from under the cursor would be a sheet nobody could finish typing in.
 */
function placed(draft: Draft): React.CSSProperties {
  const width = 21
  const left = Math.max(8, Math.min(draft.at.x + 12, window.innerWidth - width * 16 - 8))
  const top = Math.max(8, Math.min(draft.at.y - 24, window.innerHeight - 340))
  return { left: `${String(left)}px`, top: `${String(top)}px`, width: `${String(width)}rem` }
}
